import ReactDOM from 'react-dom/client';
import { nanoid } from '@reduxjs/toolkit';
import BludataPasswordRequired from './BludataPasswordRequired';

/**
 * Funzione async per richiedere la password all'utente.
 * Utilizza il componente BludataPasswordRequired esistente.
 *
 * @returns Promise<string | null> - Ritorna il token se l'utente conferma, null se annulla
 *
 * @example
 * ```tsx
 * const token = await BluAsyncPasswordDialog();
 * if (token) {
 *   // Utente ha confermato con password
 *   console.log('Token ricevuto:', token);
 * } else {
 *   // Utente ha annullato
 *   console.log('Operazione annullata');
 * }
 * ```
 */
export default function BluAsyncPasswordDialog(): Promise<string | null> {
  const appRoot = document.getElementById('root');
  const isDebug = import.meta.env.DEV;

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // In debug mode, restituisci immediatamente un token fittizio
  // (stesso comportamento di BludataPasswordRequired)
  if (isDebug) {
    return Promise.resolve('token');
  }

  return new Promise<string | null>((resolve) => {
    const newRootDiv = document.createElement('div');
    newRootDiv.id = 'temp-react-async-password-container-' + nanoid();
    document.body.appendChild(newRootDiv);
    const newRoot = ReactDOM.createRoot(newRootDiv);

    const destroyTempRoot = () => {
      newRoot.unmount();
      document.body.removeChild(newRootDiv);
      if (appRoot) {
        appRoot.style.pointerEvents = 'auto';
      }
    };

    const handleConfirm = (token: string) => {
      resolve(token);
      destroyTempRoot();
    };

    const handleClose = () => {
      resolve(null);
      destroyTempRoot();
    };

    // Blocca interazione con l'app sottostante (come BluAsyncDialog)
    if (appRoot) {
      appRoot.style.pointerEvents = 'none';
    }

    newRoot.render(
      <BludataPasswordRequired
        show={true}
        handleConfirm={handleConfirm}
        handleClose={handleClose}
      />,
    );
  });
}
